import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  VStack,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import { MdExpandMore } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import withAuth from '../../../server/libs/withAuth';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isDoctorRecruiterAccount,
  isLicenseVerified as isLicenseVerifiedFunc,
  isPublisherAccount,
} from '../../../common/libs/user';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import useSpecialPage from '../../../hooks/useSpecialPage';
import FullWidthBox from '../../../components/FullWidthBox';
import { generateS3DownloadUrl } from '../../../server/libs/siteSettings';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { getJsonFileFromS3 } from '../../../server/libs/aws';
import { SPECIAL_PAGE_GA_CATEGORY } from '../../../common/constants';
import { Props as ArticleHeader } from '../../../components/ArticleSummary';
import ArticlesGrid from '../../../components/ArticlesGrid';
import articlesInSpecialPage from '../../../server/libs/articlesInSpecialPage';

const specialPageSlug = 'cardiovascular-week';
const specialPagePath = `/special/${specialPageSlug}`;
const SITE_SETTING_JSON_S3_KEY = `special_articles/${specialPageSlug}/${specialPageSlug}_settings.json`;

const userAccessHistoryOperation = `special_cvWeek`; // user_access_histories.operationカラムの値のため合計16文字以内

type Supervisor = {
  imgS3Key: string;
  name: string;
  honorific: string;
  profile: string;
  comment: {
    title: string;
    text: string;
  };
};

type History = {
  year: number;
  subject: string;
  description: string;
  keywords: string[];
};

type Theme = {
  subject: string;
  hidden?: boolean; // true で「近日公開」状態に
  color: string;
};

type HistoryTheme = Theme & {
  description: string;
  history: History[];
  supervisor: Supervisor;
  webinar: {
    imgS3Key: string;
    date: string;
    title: string;
    lecturer: {
      name: string;
      honorific: string;
      affiliation: string;
    };
    buttonLabel?: string;
    gaEventLabel: string;
    url: string;
  };
};

// スライド一覧表示のみのテーマ
type SlideListTheme = Theme & {
  articleIds: string[];
  articles?: ArticleHeader[]; // サーバーから取得したarticlesをフロントで保持するためのフィールド JSONデータに記載不要
};

const isHistoryTheme = (
  theme: HistoryTheme | SlideListTheme,
): theme is HistoryTheme => (theme as HistoryTheme).history !== undefined;
const isSlideListTheme = (
  theme: HistoryTheme | SlideListTheme,
): theme is SlideListTheme =>
  (theme as SlideListTheme).articleIds !== undefined;

type DeviceSpecificImage = {
  pc: {
    imageS3Key: string;
  };
  mobile: {
    imageS3Key: string;
  };
};

type Settings = {
  heroContent: {
    title: string;
    mainImage: DeviceSpecificImage;
    bgImage: DeviceSpecificImage;
    description: string;
  };
  message: {
    iconImageS3Key: string | null;
    text: string; // markdown形式のテキスト
    caption: string[];
  };
  themes: (HistoryTheme | SlideListTheme)[];
  // 企業コンテンツ
  corporationArticles: {
    listTitle: string;
    articleIds: string[];
  };
};

type PageContext = {
  title: string | null;
  isMobile: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isLicenseVerifiedDoctor: boolean;
  isAllowAccount: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isPrivate: boolean;
  mainImagePath: string;
  settings: Settings;
  articles?: ArticleHeader[];
};

const HeroArea = ({
  title,
  description,
  isMobile,
  bgImage,
  mainImage,
}: {
  title: string;
  description: string;
  isMobile: boolean;
  mainImage: DeviceSpecificImage;
  bgImage: DeviceSpecificImage;
}) => (
  <FullWidthBox
    bg="#ecceff"
    backgroundImage={generateS3DownloadUrl(
      isMobile ? bgImage.mobile.imageS3Key : bgImage.pc.imageS3Key,
    )}
    backgroundSize="cover"
    backgroundPosition="center"
  >
    <Container>
      <Flex
        minH={360}
        px={4}
        py={isMobile ? 6 : 1}
        flexDirection="column"
        justifyContent="center"
        alignItems={isMobile ? 'center' : 'flex-start'}
        width={isMobile ? '100%' : '720px'}
        maxWidth="100%"
        gap={isMobile ? 4 : 0}
      >
        <Heading
          as="h2"
          color="textPrimary"
          py={isMobile ? 2 : 4}
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems={isMobile ? 'center' : 'flex-start'}
        >
          <Image
            alt={title}
            src={generateS3DownloadUrl(
              isMobile ? mainImage.mobile.imageS3Key : mainImage.pc.imageS3Key,
            )}
          />
        </Heading>
        <Box
          fontSize={isMobile ? 'sm' : 'md'}
          padding={4}
          borderRadius={4}
          bgColor="rgba(255, 255, 255, 0.4)"
        >
          {description}
        </Box>
      </Flex>
    </Container>
  </FullWidthBox>
);

const Message = ({
  iconImageS3Key,
  text,
  caption,
}: {
  iconImageS3Key: string | null;
  text: string;
  caption: string[];
}) => (
  <VStack gap={8} alignItems="center" mx={-2} py={4}>
    <Grid
      templateColumns={{
        base: '58px 1fr',
        md: '83px 1fr',
      }}
      gridRowGap={2}
      gridColumnGap={4}
      backgroundColor="white"
      py={4}
      px={{
        base: 2,
        md: 8,
      }}
    >
      <GridItem>
        <Image
          src={generateS3DownloadUrl(iconImageS3Key ?? '')}
          alt={caption.join('')}
          height={{
            base: 58,
            md: 83,
          }}
          rounded="full"
        />
      </GridItem>
      <GridItem display="flex" flexDirection="column" gap={2}>
        <Box
          fontSize={{
            base: 'sm',
            md: 'md',
          }}
        >
          <ReactMarkdown>{text}</ReactMarkdown>
        </Box>
        <Box fontSize="sm">
          <Text as="span" fontWeight="bold">
            全体監修：
          </Text>
          {caption.join('')}
        </Box>
      </GridItem>
    </Grid>
  </VStack>
);

const themeElementId = (index: number) => `theme-${index + 1}`;

const Nav = ({
  items,
}: {
  items: { subject: string; color: string; hidden?: boolean }[];
}) => {
  const onClick = (index: number) => {
    const themeElement = document.getElementById(themeElementId(index));
    if (themeElement) {
      window.scrollTo({
        top: themeElement.offsetTop - 70, // ヘッダーの高さ分調整
        behavior: 'smooth',
      });
    }
  };
  return (
    <Grid
      templateColumns={{
        base: '1fr',
        sm: 'repeat(2, 1fr)',
        md: `repeat(${items.length}, 1fr)`,
      }}
      gap={{
        base: 2,
        sm: 3,
        md: 4,
      }}
      py={4}
      px={2}
    >
      {items.map(({ subject, color, hidden }, index) => (
        <Button
          key={index}
          bgColor={hidden ? 'gray.400' : color}
          color="white"
          isDisabled={hidden}
          onClick={() => onClick(index)}
          fontSize={{
            base: 'md',
            sm: '2xl',
            md: '3xl',
          }}
          py={{
            base: 4,
            sm: 6,
            md: 8,
          }}
          boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
          _hover={{ opacity: '0.8' }}
          display="flex"
          gap={{
            base: 1,
            md: 3,
          }}
        >
          <Text>{subject}</Text>
          <Box bgColor="white" rounded="full" fontSize="2xl">
            <MdExpandMore color={hidden ? 'gray' : color} />
          </Box>
        </Button>
      ))}
    </Grid>
  );
};

const HistoryAccordion = ({ history }: { history: History[] }) => {
  return (
    <Accordion
      defaultIndex={[0]}
      allowMultiple
      allowToggle
      borderStyle="none"
      my={-3}
    >
      {history.map(({ year, subject, description, keywords }, index) => (
        <AccordionItem key={index} border="none">
          {({ isExpanded }) => (
            <>
              <AccordionButton
                key={year}
                px={0}
                py={3}
                _hover={{ bg: 'transparent', cursor: 'pointer' }}
              >
                <Heading
                  as="h4"
                  fontSize="lg"
                  fontWeight="bold"
                  display="flex"
                  textAlign="left"
                  alignItems="center"
                  gap={2}
                >
                  <Text as="span">{isExpanded ? '▼' : '▶'}</Text>
                  <Text as="span">
                    {year}年：{subject}
                  </Text>
                </Heading>
              </AccordionButton>
              <AccordionPanel py={0} pl={6}>
                <Box>{description}</Box>
                <Flex my={2} gap={2}>
                  <Box fontWeight="bold">
                    <Text as="span" whiteSpace="nowrap">
                      キーワード:
                    </Text>
                    {keywords.map((keyword, i) => (
                      <Text as="span" key={i} color="primaryMain" px={1}>
                        #{keyword}
                      </Text>
                    ))}
                  </Box>
                </Flex>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      ))}
    </Accordion>
  );
};

const ThemeSuperVisor = ({
  supervisor: { imgS3Key, name, honorific, profile, comment },
}: {
  supervisor: Supervisor;
}) => (
  <Flex
    border="1px solid"
    borderColor="borderLight"
    borderRadius={8}
    p={6}
    gap={6}
    flexDir={{
      base: 'column',
      md: 'row',
    }}
  >
    <Flex gap={6} alignItems="center">
      <Image
        src={generateS3DownloadUrl(imgS3Key ?? '')}
        alt={`${name}${honorific ?? ''}`}
        height={100}
        rounded="full"
      />
      <Box>
        <Heading
          as="h4"
          size="md"
          gap={1}
          display="flex"
          flexDirection="column"
          mb={2}
        >
          <Text>監修</Text>
          {name}
          {honorific}
        </Heading>
        <Text fontSize="sm">{profile}</Text>
      </Box>
    </Flex>
    <Box
      width={{
        md: '65%',
      }}
    >
      <Heading as="h4" fontSize="md" pb={1}>
        {comment.title}
      </Heading>
      <Box fontSize="sm" whiteSpace="pre-wrap">
        {comment.text}
      </Box>
    </Box>
  </Flex>
);

const Webinar = ({
  webinar: { imgS3Key, date, title, lecturer, url, gaEventLabel, buttonLabel },
}: {
  webinar: HistoryTheme['webinar'];
}) => (
  <Flex flexDirection="column" gap={1} mb={12}>
    <Heading
      as="h3"
      size={{
        base: 'md',
        sm: 'lg',
      }}
      textAlign="center"
      color="primaryMain"
    >
      ＼解説ウェビナーを開催！/
    </Heading>
    <Flex
      backgroundColor="#EEF6ED"
      p={6}
      gap={6}
      flexDirection={{
        base: 'column',
        md: 'row',
      }}
    >
      <Image
        src={generateS3DownloadUrl(imgS3Key ?? '')}
        alt="解説ウェビナーバナー"
        height={{
          base: 'auto',
          lg: 225,
        }}
        width={{
          base: '100%',
          md: '50%',
          lg: 'auto',
        }}
      />
      <Box>
        <Heading as="h4" size="sm">
          <Box
            fontSize={{
              base: 'xl',
              md: '3xl',
            }}
            pb={1}
          >
            {date}
          </Box>
          <Box
            fontSize={{
              base: 'md',
              md: 'xl',
            }}
            whiteSpace="pre-wrap"
            lineHeight="1.5"
          >
            {title}
          </Box>
        </Heading>
        <Box pt={1} pb={5}>
          講師：{lecturer.name}
          {lecturer.honorific}（{lecturer.affiliation}）
        </Box>
        <Button
          as="a"
          colorScheme="brand"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          gap={1}
          onClick={() =>
            sendAnalyticsEvent(
              SPECIAL_PAGE_GA_CATEGORY,
              'click',
              `${specialPageSlug}_webinar_${gaEventLabel}`,
            )
          }
        >
          {buttonLabel ?? '詳細はこちら'}
        </Button>
      </Box>
    </Flex>
  </Flex>
);

const HistoryThemeComponent = ({
  subject,
  description,
  history,
  supervisor,
  webinar,
  index,
  hidden,
  isMobile,
}: HistoryTheme & {
  index: number;
  isMobile: boolean;
}) => {
  return (
    <Flex flexDirection="column" gap={6} mt={isMobile ? 2 : 8}>
      <Heading
        as="h3"
        id={themeElementId(index)}
        fontSize={isMobile ? 'lg' : 'xl'}
        mt={6}
        py={1}
        pl={4}
        borderLeft="4px solid"
        borderColor="primaryMain"
      >
        {subject} の変遷を振り返る
      </Heading>
      {hidden ? (
        <Box mb={20} color="gray.500" fontSize="lg">
          近日公開
        </Box>
      ) : (
        <>
          <Box>{description}</Box>
          <HistoryAccordion history={history} />
          <ThemeSuperVisor supervisor={supervisor} />
          <Webinar webinar={webinar} />
        </>
      )}
    </Flex>
  );
};

const SlideListThemeComponent = ({
  index,
  subject,
  hidden,
  isMobile,
  articles,
}: SlideListTheme & {
  index: number;
  isMobile: boolean;
  articles: ArticleHeader[];
}) => {
  return (
    <Flex flexDirection="column" gap={6} mt={isMobile ? 2 : 8} mb={20}>
      <Heading
        as="h3"
        id={themeElementId(index)}
        fontSize={isMobile ? 'lg' : 'xl'}
        mt={6}
        py={1}
        pl={4}
        borderLeft="4px solid"
        borderColor="primaryMain"
      >
        {subject}
      </Heading>
      {hidden ? (
        <Box mb={20} color="gray.500" fontSize="lg">
          近日公開
        </Box>
      ) : (
        <ArticlesGrid
          articles={articles}
          isMobile={isMobile}
          gaEventLabel={`${specialPageSlug}_articles`}
          onShowThumbnail={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'show',
              `special_${specialPageSlug}#${articleId}`,
            );
          }}
          onClick={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'click',
              `special_${specialPageSlug}#${articleId}`,
            );
          }}
        />
      )}
    </Flex>
  );
};

const CardiovascularWeekPage: NextPage<PageContext> = ({
  title: pageTitle = '',
  isMobile,
  isAuthenticated,
  isAllowAccount,
  isLicenseVerifiedDoctor,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
  mainImagePath,
  settings,
  articles,
}) => {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle: pageTitle ?? '',
    gaLabelKey: specialPageSlug,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    isAllowAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  if (isBlocked) {
    return blockedContent;
  }

  const { message, themes, heroContent, corporationArticles } = settings;

  // スライドリストを表示するテーマにarticlesを割り当て表示するスライドがないテーマはテーマごと削除
  const filteredTheme = themes
    .map((theme) => {
      if (isSlideListTheme(theme)) {
        theme.articles = [];
        theme.articles = articles?.filter((article) =>
          theme.articleIds.includes(article.articleId),
        );
        // articleIdsの順に並び替える
        theme.articles?.sort(
          (a, b) =>
            theme.articleIds.indexOf(a.articleId) -
            theme.articleIds.indexOf(b.articleId),
        );
      }
      return theme;
    })
    .filter((theme) => {
      if (isSlideListTheme(theme)) {
        return !!theme.articles?.length;
      }
      return true;
    });

  const shouldShowCorporationContents =
    (isAllowAccount || isLicenseVerifiedDoctor) &&
    !!articles?.length &&
    !!corporationArticles?.articleIds?.length;

  const corporationContents = shouldShowCorporationContents && (
    <FullWidthBox bg="backgroundLight">
      <Container>
        <Box py={8}>
          <Heading as="h3" fontSize="xl" fontWeight="bold" mb={6}>
            {corporationArticles.listTitle}
          </Heading>
          <ArticlesGrid
            articles={articles.filter((article) =>
              corporationArticles.articleIds.includes(article.articleId),
            )}
            isMobile={isMobile}
            gaEventLabel={`${specialPageSlug}_corpArticles`}
            onShowThumbnail={(articleId) => {
              sendAnalyticsEvent(
                'slide_thumbnail',
                'show',
                `special_${specialPageSlug}#${articleId}`,
              );
            }}
            onClick={(articleId) => {
              sendAnalyticsEvent(
                'slide_thumbnail',
                'click',
                `special_${specialPageSlug}#${articleId}`,
              );
            }}
          />
        </Box>
      </Container>
    </FullWidthBox>
  );

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white" mb={-16}>
        {breadcrumbs}
        <HeroArea
          title={heroContent.title}
          description={heroContent.description}
          isMobile={isMobile}
          bgImage={heroContent.bgImage}
          mainImage={heroContent.mainImage}
        />
        <FullWidthBox
          bg={'#EEF6ED'}
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Container>
            <Message {...message} />
          </Container>
        </FullWidthBox>
        <Box px={[4, 6, 0]}>
          <Nav items={filteredTheme} />
          {filteredTheme.map((theme, index) => (
            <Box key={index} p={2}>
              {isHistoryTheme(theme) && (
                <HistoryThemeComponent
                  {...theme}
                  index={index}
                  isMobile={isMobile}
                />
              )}
              {isSlideListTheme(theme) && !!theme.articles?.length && (
                <SlideListThemeComponent
                  {...theme}
                  index={index}
                  isMobile={isMobile}
                  articles={theme.articles}
                />
              )}
            </Box>
          ))}
        </Box>
        {corporationContents}
      </Box>
    </>
  );
};

export default CardiovascularWeekPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    void createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    }).catch((e) => {
      console.error('ユーザーアクセス履歴の保存に失敗しました。', e);
    });

    const { isPrivate, canDisplay, title, topImage, isAllowAccount } =
      await getSpecialArticleProps(specialPageSlug, userId);
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    const mainImagePath: string = generateS3DownloadUrl(topImage);
    let settings: Settings | null = null;
    try {
      settings = (await getJsonFileFromS3(
        SITE_SETTING_JSON_S3_KEY,
      )) as Settings;
    } catch (e) {
      console.error('サイト設定の取得に失敗しました。', e);
    }

    if (!settings) {
      return {
        notFound: true,
      };
    }

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    const isAuthenticated = context.req.isAuthenticated();
    const isLicenseVerified = isLicenseVerifiedFunc(context.req.user ?? null);
    const isLicenseVerifiedDoctor =
      context.req.user?.antaaAccountType === AntaaAccountType.DOCTOR &&
      isLicenseVerified;

    let themeArticleIds: string[] =
      settings?.themes.flatMap((theme) =>
        isSlideListTheme(theme) ? theme?.articleIds ?? [] : [],
      ) ?? [];

    let corporationArticleIds: string[] =
      settings?.corporationArticles?.articleIds ?? [];
    if (!isAllowAccount && !isLicenseVerifiedDoctor) {
      corporationArticleIds = [];
    }

    const articleIdList: string[] = [
      ...themeArticleIds,
      ...corporationArticleIds,
    ];

    // articleIdListの重複を排除
    const uniqueArticleIdList = [...new Set(articleIdList)];

    const articles = await articlesInSpecialPage({
      articleIdList: uniqueArticleIdList,
      isAuthenticated,
      isAllowAccount,
      user: context.req.user,
    });

    return {
      props: {
        title,
        isMobile,
        isDemoAccount,
        isAuthenticated,
        isLicenseVerified,
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isLicenseVerifiedDoctor,
        isAllowAccount,
        isPrivate,
        mainImagePath,
        settings,
        articles,
      },
    };
  },
);
